/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Config needed to inject the Fibbl runtime. Mirrors the former `FibblScriptLoader` props.
 */
export interface FibblRuntimeConfig {
    /**
     * Dev/staging token from app.fibbl.com → Tech settings. Omit in production — domain-based auth is
     * used automatically. Never expose a real token on a publicly deployed site.
     */
    token?: string;
    /** CSP nonce from the root loader — required when the app enforces script-src nonces. */
    nonce?: string;
    /** BCP-47 locale forwarded to Fibbl's UI. */
    locale: string;
}

interface FibblRuntimeValue {
    /** True once a shopper has activated a 3D/AR surface and the runtime has been requested. */
    requested: boolean;
    /** Request the runtime: injects the ~305KB Fibbl CDN script (idempotent) and flips `requested`. */
    request: () => void;
}

/**
 * Injects the Fibbl CDN script once per page load. Idempotent — the `data-fibbl-config` guard makes
 * repeat calls (e.g. desktop tile + mobile hero both activating) no-ops. Fibbl requires exactly one
 * script tag carrying `data-fibbl-config`, and it must remain in the DOM for the session because the
 * custom elements rely on it.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function loadFibblRuntime({ token, nonce, locale }: FibblRuntimeConfig): void {
    if (typeof document === 'undefined') return;
    if (document.querySelector('[data-fibbl-config]')) return;
    const script = document.createElement('script');
    script.src = 'https://cdn.fibbl.com/fibbl.js';
    script.type = 'module';
    script.setAttribute('data-fibbl-config', '');
    script.setAttribute('data-locale', locale);
    if (token) script.setAttribute('data-token', token);
    if (nonce) script.nonce = nonce;
    document.head.appendChild(script);
}

const FibblRuntimeContext = createContext<FibblRuntimeValue>({ requested: false, request: () => undefined });

/**
 * Provides on-demand ("click to load") access to the Fibbl runtime for a subtree (the product
 * gallery). The ~305KB runtime is NOT injected on mount or on idle — it loads only when a shopper
 * activates a 3D surface (clicks the desktop "View in 3D" tile, or selects the 3D thumbnail on
 * mobile). On the PDP the 3D tile is above the fold on desktop, so idle-loading would still pull the
 * runtime + model assets on every Fibbl product even when the shopper never engages 3D; gating on
 * interaction keeps it off the critical path entirely until it's actually wanted (CLAUDE.md rule #20).
 *
 * Pass `config = null` to render an inert provider — `request()` becomes a no-op, so callsites don't
 * need to branch on whether Fibbl is active for the current product.
 */
export function FibblRuntimeProvider({ config, children }: { config: FibblRuntimeConfig | null; children: ReactNode }) {
    const [requested, setRequested] = useState(false);
    // Destructure to primitives so `request`/`value` don't re-create every render when the parent
    // passes a fresh `config` object literal. `enabled` is a stable boolean while Fibbl is active.
    const enabled = Boolean(config);
    const token = config?.token;
    const nonce = config?.nonce;
    const locale = config?.locale;
    const request = useCallback(() => {
        if (!enabled) return;
        setRequested(true);
        loadFibblRuntime({ token, nonce, locale: locale ?? 'en-US' });
    }, [enabled, token, nonce, locale]);
    const value = useMemo<FibblRuntimeValue>(
        () => ({ requested: enabled && requested, request }),
        [enabled, requested, request]
    );
    return <FibblRuntimeContext.Provider value={value}>{children}</FibblRuntimeContext.Provider>;
}

/**
 * Read the Fibbl runtime state for the current subtree. Outside a provider (e.g. the gallery in a
 * cart/bonus modal) this returns the inert default, so 3D surfaces simply never activate.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function useFibblRuntime(): FibblRuntimeValue {
    return useContext(FibblRuntimeContext);
}
